import React, { useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { ChevronDown, Loader2, Search, X } from 'lucide-react'

/**
 * Props:
 *  - userId: number
 *  - onSelect: (product|null) => void
 *  - refreshFlag: any
 *  - value?: { sku, title } | null
 */
const ProductDropdown = ({ userId, onSelect, refreshFlag, value = null }) => {
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)
  const baseUrl = process.env.REACT_APP_API_URL || ''

  useEffect(() => {
    if (!userId) return
    setLoading(true)
    axios.get(`${baseUrl}/api/products`, { params: { user_id: userId } })
      .then(res => {
        const sorted = res.data.products.slice().sort((a, b) => {
          const ca = (a.categories?.[0] || '').toLowerCase()
          const cb = (b.categories?.[0] || '').toLowerCase()
          if (ca < cb) return -1
          if (ca > cb) return 1
          return a.title.localeCompare(b.title)
        })
        setOptions(sorted)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [userId, refreshFlag, baseUrl])

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const valueObj = value
    ? options.find(option => option.sku === value.sku) || null
    : null

  const filteredOptions = useMemo(() => {
    const term = query.trim().toLowerCase()
    if (!term) return options

    return options.filter(option => {
      const title = option.title || ''
      const sku = option.sku || ''
      const category = option.categories?.[0] || ''
      return `${title} ${sku} ${category}`.toLowerCase().includes(term)
    })
  }, [options, query])

  const groupedOptions = useMemo(() => {
    return filteredOptions.reduce((groups, option) => {
      const category = option.categories?.[0] || 'Otros'
      if (!groups[category]) groups[category] = []
      groups[category].push(option)
      return groups
    }, {})
  }, [filteredOptions])

  const inputValue = isOpen
    ? query
    : valueObj
      ? `${valueObj.title} (${valueObj.sku})`
      : query

  const handleSelect = product => {
    onSelect(product)
    setQuery('')
    setIsOpen(false)
  }

  const handleClear = event => {
    event.stopPropagation()
    onSelect(null)
    setQuery('')
    setIsOpen(false)
  }

  return (
    <div ref={dropdownRef} style={styles.container}>
      <label style={styles.label} htmlFor="product-dropdown-input">
        Selecciona un producto
      </label>

      <div style={styles.inputShell} onClick={() => setIsOpen(true)}>
        <Search size={16} color="#6b7280" />
        <input
          id="product-dropdown-input"
          type="text"
          value={inputValue}
          placeholder="Busca por nombre o sku"
          onChange={event => {
            setQuery(event.target.value)
            setIsOpen(true)
          }}
          onFocus={() => {
            setQuery('')
            setIsOpen(true)
          }}
          style={styles.input}
        />

        {loading ? (
          <Loader2 size={16} color="#6b7280" style={styles.spinIcon} />
        ) : valueObj ? (
          <button type="button" onClick={handleClear} style={styles.iconButton} title="Limpiar">
            <X size={16} />
          </button>
        ) : (
          <ChevronDown size={18} color="#6b7280" />
        )}
      </div>

      {isOpen && (
        <div style={styles.menu}>
          {loading ? (
            <div style={styles.emptyState}>Cargando productos...</div>
          ) : filteredOptions.length === 0 ? (
            <div style={styles.emptyState}>No se encontraron productos</div>
          ) : (
            Object.entries(groupedOptions).map(([category, products]) => (
              <div key={category}>
                <div style={styles.groupLabel}>{category}</div>
                {products.map(product => (
                  <button
                    key={product.sku}
                    type="button"
                    style={{
                      ...styles.option,
                      ...(valueObj?.sku === product.sku ? styles.selectedOption : {})
                    }}
                    onClick={() => handleSelect(product)}
                  >
                    <span style={styles.optionTitle}>{product.title}</span>
                    <span style={styles.optionSku}>{product.sku}</span>
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}

const styles = {
  container: {
    position: 'relative',
    width: '100%'
  },
  label: {
    color: '#374151',
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 6
  },
  inputShell: {
    alignItems: 'center',
    background: '#fff',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    display: 'flex',
    gap: 8,
    minHeight: 40,
    padding: '0 10px',
    width: '100%'
  },
  input: {
    border: 0,
    color: '#111827',
    flex: 1,
    fontSize: 14,
    minWidth: 0,
    outline: 'none',
    padding: '9px 0'
  },
  iconButton: {
    alignItems: 'center',
    background: 'transparent',
    border: 0,
    borderRadius: 6,
    color: '#6b7280',
    cursor: 'pointer',
    display: 'flex',
    height: 28,
    justifyContent: 'center',
    padding: 0,
    width: 28
  },
  spinIcon: {
    animation: 'spin 1s linear infinite'
  },
  menu: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    boxShadow: '0 16px 32px rgba(15, 23, 42, 0.14)',
    left: 0,
    marginTop: 6,
    maxHeight: 280,
    overflowY: 'auto',
    position: 'absolute',
    right: 0,
    zIndex: 50
  },
  groupLabel: {
    background: '#f9fafb',
    color: '#6b7280',
    fontSize: 12,
    fontWeight: 700,
    padding: '8px 12px',
    textTransform: 'uppercase'
  },
  option: {
    alignItems: 'flex-start',
    background: '#fff',
    border: 0,
    borderBottom: '1px solid #f3f4f6',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: '10px 12px',
    textAlign: 'left',
    width: '100%'
  },
  selectedOption: {
    background: '#eef6ff'
  },
  optionTitle: {
    color: '#111827',
    fontSize: 14,
    fontWeight: 600
  },
  optionSku: {
    color: '#6b7280',
    fontSize: 12
  },
  emptyState: {
    color: '#6b7280',
    fontSize: 14,
    padding: 14,
    textAlign: 'center'
  }
}

export default ProductDropdown
